/**
 * The confidence tag on a report item — icon + basis + level, never a colour on
 * its own and never a traffic-light token. The table lives in `confidence.ts`
 * so a test can import it without rendering.
 */
import { Icon } from '@/components/Icon'
import { CONFIDENCE_LOOK, confidenceOf, type ReportLane } from '@/features/report/confidence'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/utils'

export function ConfidenceTag({ lane, className }: { lane: ReportLane; className?: string }) {
  const { t } = useI18n()
  const basis = confidenceOf(lane)
  const look = CONFIDENCE_LOOK[basis]

  return (
    <span
      data-testid="confidence-tag"
      data-confidence-basis={basis}
      title={t(look.meaning)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-0.5 text-[12px] leading-5',
        look.className,
        className,
      )}
    >
      <Icon icon={look.icon} className="size-3.5" />
      <span className="font-medium">{t(look.basis)}</span>
      <span aria-hidden="true" className="text-muted-2">
        ·
      </span>
      <span>{t(look.level)}</span>
      {/* Decorative meter: the words above are the accessible content. */}
      <span aria-hidden="true" className="ms-0.5 inline-flex items-end gap-0.5">
        {[1, 2, 3].map((step) => (
          <span
            key={step}
            className={cn(
              'w-1 rounded-xs',
              step === 1 ? 'h-1.5' : step === 2 ? 'h-2.5' : 'h-3.5',
              step <= look.steps ? 'bg-ink-2' : 'bg-border',
            )}
          />
        ))}
      </span>
    </span>
  )
}
